import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { accumulate } from '@olivierpascal/helpers';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiClientAuthorization } from '~/lib/api';
import type { IAppRegion } from '~/lib/appSettings';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { ApiError } from '~/lib/api';
import { useLogInMutation } from '~/lib/api/modules/Auth/hooks/useLogInMutation';
import { getAppSettings } from '~/lib/appSettings';
import { storageFactory } from '~/lib/storage';
import { LogInFormMainSection } from './LogInFormMainSection';

export type ILogInFormValues = {
  appRegion?: IAppRegion;
  email?: string;
  password?: string;
};

export type ILogInFormProps = IContainerProps & {
  onSuccess?: (authorization: IApiClientAuthorization) => void;
  cancelUrl?: string;
  defaultValues?: DefaultValues<ILogInFormValues>;
};

export const LogInForm: React.FC<ILogInFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, defaultValues } = props;
  const { t } = useTranslation();
  const logInMutation = useLogInMutation();
  const appSettings = getAppSettings();
  const defaultAppRegion = appSettings.defaultRegion;
  const storageRef = useRef(storageFactory('local')());
  const form = useForm<ILogInFormValues>({
    defaultValues: accumulate(
      {
        appRegion:
          storageRef.current.get('preferredAppRegion') ?? defaultAppRegion,
      },
      defaultValues,
    ),
  });
  useFormAutoFocus({ form, name: 'email' });

  const appRegion = form.watch('appRegion');
  useEffect(() => {
    if (appRegion) {
      void storageRef.current.set('preferredAppRegion', appRegion);
    }
  }, [appRegion]);

  const handleSubmit: SubmitHandler<ILogInFormValues> = (values) => {
    if (logInMutation.isLoading) {
      return;
    }

    return logInMutation
      .mutateAsync({
        region: values.appRegion ?? defaultAppRegion,
        body: {
          email: values.email ?? '',
          password: values.password ?? '',
        },
      })
      .then(onSuccess)
      .catch((error) => {
        if (error instanceof ApiError) {
          const areCredentialsInvalid = error.errors.some((error) =>
            ['incorrect_password', 'user_not_found'].includes(error.code),
          );

          if (areCredentialsInvalid) {
            throw new Error(t('LogInForm.invalid-credentials'));
          }
        }

        throw error;
      });
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.FormBody
        form={form}
        cancelUrl={cancelUrl}
        onSubmit={handleSubmit}
        submitButtonLabel={t('LogInForm.log-in')}
        first
      >
        <LogInFormMainSection form={form} defaultValues={defaultValues} />
      </ContentBlock.FormBody>
    </ContentBlock>
  );
};
